// src/components/statistiche/RilevatoriView.js
import React, { useEffect, useMemo, useState } from "react";
import { Bar, Line, Pie, Radar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  RadialLinearScale,
  PointElement,
  Filler,
} from "chart.js";

// registriamo qui gli elementi che servono a Pie e Radar
ChartJS.register(ArcElement, RadialLinearScale, PointElement, Filler);



/* Piccola chart inline “sparkline” per il trend Real per rilevatore */
function Sparkline({
  values,
  width = 80,
  height = 18,
  stroke = "#22c55e",
}) {
  if (!Array.isArray(values) || values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;

  const stepX = width / (values.length - 1);

  const points = values
    .map((v, i) => {
      const x = i * stepX;
      const norm = (v - min) / span; // [0..1]
      // un filo di padding top/bottom
      const y = height - norm * (height - 2) - 1;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
    >
      <polyline
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
    </svg>
  );
}















/**
 * data: { summary, rows, timeline, timelinePoints }
 * loading: boolean
 */
export default function RilevatoriView({ data, loading }) {
  const rawRows = Array.isArray(data?.rows) ? data.rows : [];

  // Normalizzo righe e campi numerici
  const rows = useMemo(
  () =>
    rawRows.map((r) => ({
      ...r,
      id: String(r.id ?? r.ID ?? r.user_id ?? r.nome ?? Math.random()),
      nome: r.nome || r.label || "—",

      // conteggi base
      appuntamenti: Number(r.appuntamenti ?? 0),
      non_buoni: Number(r.non_buoni ?? r.ril_non_buono ?? 0),
      passa_senior: Number(r.passa_senior ?? r.passa_al_senior ?? 0),

      // esiti senior
      rilievo_chiusi: Number(r.rilievo_chiusi ?? r.ril_chiusi ?? 0),
      rilievo_non_chiusi: Number(
        r.rilievo_non_chiusi ?? r.ril_non_chiusi ?? 0
      ),

      senior_visite: Number(r.senior_visite ?? 0),
      senior_annullati: Number(r.senior_annullati ?? 0),

      // numeri “grossi”
      fatturato_eur: Number(r.fatturato_eur ?? r.real_eur ?? 0),
      km: Number(r.km ?? r.km_tot ?? 0),

      // 👇 NUOVO: trend per sparkline
      trend_real: Array.isArray(r.trend_real)
        ? r.trend_real.map((v) => Number(v) || 0)
        : [],
    })),
  [rawRows]
);


  // ================== SELEZIONE RILEVATORI ==================
  const [visibleIds, setVisibleIds] = useState(new Set());

  useEffect(() => {
    // di default tutti visibili
    setVisibleIds(new Set(rows.map((r) => r.id)));
  }, [rows]);

  const toggleRilevatore = (id) => {
    setVisibleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const showAll = visibleIds.size === 0 || visibleIds.size === rows.length;

  // righe effettivamente visibili (ordinate per appuntamenti) — **TOP 10**
  const activeRows = useMemo(() => {
    const activeSet = showAll ? new Set(rows.map((r) => r.id)) : visibleIds;
    return [...rows]
      .filter((r) => activeSet.has(r.id))
      .sort((a, b) => b.appuntamenti - a.appuntamenti)
      .slice(0, 10); // top 10
  }, [rows, visibleIds, showAll]);

  // Altezza del grafico: sufficiente per far respirare i nomi
  const chartHeight = Math.max(320, activeRows.length * 52);

  // ================== MODALITÀ METRICHE ==================
  // valueMode:
  //  - "counts" → App / Non buoni / Passa al sen.
  //  - "real"   → solo Real €
  //  - "km"     → solo Km
  // ================== MODALITÀ METRICHE ==================
// barMode controlla SOLO il grafico a barre di sinistra
//  - "counts" → App / NB / PAS
//  - "real"   → solo Real (€)
//  - "km"     → solo Km
const [barMode, setBarMode] = useState("counts");

// cosa mostra la torta (lascialo com’è: real | app | km)
const [pieMode, setPieMode] = useState("real");

// quali contatori mostrare quando siamo in modalità "counts"
const [countMetrics, setCountMetrics] = useState({
  app: true,
  nonBuoni: true,
  passa: true,
});

// toggle per i contatori → restiamo in barMode="counts"
const toggleCountMetric = (key) => {
  setBarMode("counts");
  setCountMetrics((prev) => {
    const next = { ...prev, [key]: !prev[key] };
    // non lasciare tutti spenti
    if (!next.app && !next.nonBuoni && !next.passa) {
      next.app = true;
    }
    return next;
  });
};

// KPI "Real (€)" → switch counts <-> real
const toggleRealMode = () => {
  setBarMode((prev) => (prev === "real" ? "counts" : "real"));
};

// KPI "Km totali" → switch counts <-> km
const toggleKmMode = () => {
  setBarMode((prev) => (prev === "km" ? "counts" : "km"));
};

// ================== SUMMARY ==================
const summaryAll = {
  tot_appuntamenti: Number(data?.summary?.tot_appuntamenti ?? 0),
  tot_non_buoni: Number(data?.summary?.tot_non_buoni ?? 0),
  tot_passa_senior: Number(data?.summary?.tot_passa_senior ?? 0),
  tot_rilievo_chiusi: Number(data?.summary?.tot_rilievo_chiusi ?? 0),
  tot_rilievo_non_chiusi: Number(data?.summary?.tot_rilievo_non_chiusi ?? 0),
  tot_senior_visite: Number(data?.summary?.tot_senior_visite ?? 0),
  tot_senior_annullati: Number(data?.summary?.tot_senior_annullati ?? 0),
  fatturato_eur: Number(data?.summary?.fatturato_eur ?? 0),
  km_tot: Number(data?.summary?.km_tot ?? 0),
};

const summarySelected = useMemo(() => {
  if (showAll || activeRows.length === 0) return summaryAll;

  return activeRows.reduce(
    (acc, r) => {
      acc.tot_appuntamenti += r.appuntamenti;
      acc.tot_non_buoni += r.non_buoni;
      acc.tot_passa_senior += r.passa_senior;
      acc.tot_rilievo_chiusi += r.rilievo_chiusi;
      acc.tot_rilievo_non_chiusi += r.rilievo_non_chiusi;
      acc.tot_senior_visite += r.senior_visite;
      acc.tot_senior_annullati += r.senior_annullati;
      acc.fatturato_eur += r.fatturato_eur;
      acc.km_tot += r.km;
      return acc;
    },
    {
      tot_appuntamenti: 0,
      tot_non_buoni: 0,
      tot_passa_senior: 0,
      tot_rilievo_chiusi: 0,
      tot_rilievo_non_chiusi: 0,
      tot_senior_visite: 0,
      tot_senior_annullati: 0,
      fatturato_eur: 0,
      km_tot: 0,
    }
  );
}, [activeRows, showAll, summaryAll]);

const anyData = rows.length > 0;

// ================== GRAFICO A BARRE ==================
const chartLabels = activeRows.map((r) => r.nome);

const appData      = activeRows.map((r) => r.appuntamenti);
const nonBuoniData = activeRows.map((r) => r.non_buoni);
const passaData    = activeRows.map((r) => r.passa_senior);
const realData     = activeRows.map((r) => r.fatturato_eur);
const kmData       = activeRows.map((r) => r.km);

const datasets = [];

if (barMode === "real") {
  // SOLO REAL (€)
  datasets.push({
    label: "Real (€)",
    data: realData,
    backgroundColor: "rgba(34,197,94,0.9)",
    borderColor: "rgba(22,163,74,1)",
    borderWidth: 1,
    borderRadius: 18,
    barThickness: 15,
    maxBarThickness: 15,
  });
} else if (barMode === "km") {
  // SOLO KM
  datasets.push({
    label: "Km",
    data: kmData,
    backgroundColor: "rgba(168,85,247,0.9)",
    borderColor: "rgba(147,51,234,1)",
    borderWidth: 1,
    borderRadius: 18,
    barThickness: 15,
    maxBarThickness: 15,
  });
} else {
  // MODALITÀ CONTEGGI (App / NB / PAS)
  if (countMetrics.app) {
    datasets.push({
      label: "Appuntamenti",
      data: appData,
      backgroundColor: "rgba(248,113,113,0.96)",
      borderColor: "rgba(220,38,38,1)",
      borderWidth: 1,
      borderRadius: 16,
      barThickness: 13,
      maxBarThickness: 13,
    });
  }
  if (countMetrics.nonBuoni) {
    datasets.push({
      label: "Non buoni",
      data: nonBuoniData,
      backgroundColor: "rgba(250,204,21,0.96)",
      borderColor: "rgba(202,138,4,1)",
      borderWidth: 1,
      borderRadius: 16,
      barThickness: 9,
      maxBarThickness: 9,
    });
  }
  if (countMetrics.passa) {
    datasets.push({
      label: "Passa al senior",
      data: passaData,
      backgroundColor: "rgba(59,130,246,0.96)",
      borderColor: "rgba(37,99,235,1)",
      borderWidth: 1,
      borderRadius: 16,
      barThickness: 9,
      maxBarThickness: 9,
    });
  }
}

 const chartData = { labels: chartLabels, datasets };

// legenda visibile SOLO quando siamo in modalità "counts" con più di un dataset
const showLegend = barMode === "counts" && datasets.length > 1;

// asse secondario solo per le modalità “real” o “km”
const useSecondAxis = barMode === "real" || barMode === "km";


  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: {
        display: showLegend,
        position: "top",
        labels: { color: "#0f172a", boxWidth: 18 },
      },
      title: {
        display: true,
        text: "Appuntamenti per risorsa (ordinati per numero di appuntamenti)",
        color: "#0f172a",
        font: { size: 18, weight: "600" },
        padding: { top: 8, bottom: 8 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const label = ctx.dataset.label || "";
            const v = Number(ctx.parsed.x ?? ctx.parsed.y ?? 0);
            if (label.includes("€")) {
              return ` ${label}: ${v.toLocaleString("it-IT", {
                style: "currency",
                currency: "EUR",
                maximumFractionDigits: 0,
              })}`;
            }
            return ` ${label}: ${v.toLocaleString("it-IT")}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { color: "rgba(148,163,184,0.25)" },
        ticks: { color: "#64748b" },
      },
      y: {
        grid: { display: false },
        ticks: {
          color: "#0f172a",
          font: { size: 15 },
          padding: 12,
        },
      },
      y1: {
        position: "top",
        display: useSecondAxis,
        grid: { display: false },
        ticks: { color: "#64748b", font: { size: 10 } },
      },
    },
  };

  // ================== TIMELINE FATTURATO ==================
  const timelineRaw = Array.isArray(data?.timeline) ? data.timeline : [];
  const timelinePointsRaw = Array.isArray(data?.timelinePoints)
    ? data.timelinePoints
    : [];

  const timelineSorted = [...timelineRaw].sort((a, b) =>
    String(a.date).localeCompare(String(b.date))
  );

  const timelineLabels = timelineSorted.map((r) => r.date);
  const dailyValues = timelineSorted.map((r) =>
    Number(r.fatturato_eur ?? 0)
  );

  const cumulativeValues = [];
  let acc = 0;
  for (const v of dailyValues) {
    acc += v;
    cumulativeValues.push(acc);
  }

  const pointsByDate = timelinePointsRaw.reduce((acc, p) => {
    const d = String(p.date);
    if (!acc[d]) acc[d] = [];
    acc[d].push({
      cliente: p.cliente || "",
      real_eur: Number(p.real_eur ?? 0),
    });
    return acc;
  }, {});

  const timelineData = {
    labels: timelineLabels,
    datasets: [
      {
        label: "Real (€) / giorno",
        data: dailyValues,
        borderColor: "rgba(248,113,113,0.9)",
        backgroundColor: "rgba(248,113,113,0.12)",
        tension: 0.2,
        fill: true,
        pointRadius: 3,
        pointHitRadius: 10,
      },
      {
        label: "Real (€) cumulato",
        data: cumulativeValues,
        borderColor: "rgba(34,197,94,0.9)",
        backgroundColor: "rgba(34,197,94,0.05)",
        tension: 0.2,
        fill: false,
        pointRadius: 3,
        pointHitRadius: 10,
      },
    ],
  };

  const timelineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true },
      title: {
        display: true,
        text: "Andamento Real (€) nel periodo",
        color: "#0f172a",
        font: { size: 18, weight: "600" },
        padding: { top: 8, bottom: 8 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const v = Number(ctx.parsed.y ?? 0);
            return ` ${ctx.dataset.label}: ${v.toLocaleString("it-IT", {
              style: "currency",
              currency: "EUR",
              maximumFractionDigits: 0,
            })}`;
          },
          footer: (items) => {
            if (!items.length) return "";
            const date = items[0].label;
            const pts = pointsByDate[date] || [];
            if (!pts.length) return "";
            return pts
              .map((p) => {
                const name = p.cliente || "Cliente";
                const euro = p.real_eur.toLocaleString("it-IT", {
                  style: "currency",
                  currency: "EUR",
                  maximumFractionDigits: 0,
                });
                return `${name}: ${euro}`;
              })
              .join("\n");
          },
        },
      },
    },
    scales: {
      x: { ticks: { color: "#64748b" }, grid: { display: false } },
      y: {
        ticks: { color: "#64748b" },
        grid: { color: "rgba(148,163,184,0.25)" },
      },
    },
  };



  // ================== RADAR: PROFILO RILEVATORE ==================
  // Mostriamo il radar solo quando è selezionato un singolo rilevatore
  const radarRow = activeRows.length === 1 ? activeRows[0] : null;

  let radarData = null;
  let radarOptions = null;

  if (radarRow) {
    // normalizziamo rispetto al migliore, per avere scala 0–100
    const maxApp = rows.reduce(
      (m, r) => Math.max(m, Number(r.appuntamenti || 0)),
      1
    );

    const maxRealPerApp = rows.reduce((m, r) => {
      const app = Number(r.appuntamenti || 0);
      const real = Number(r.fatturato_eur || 0);
      const val = app > 0 ? real / app : 0;
      return Math.max(m, val);
    }, 1);

    const app = Number(radarRow.appuntamenti || 0);
    const passa = Number(radarRow.passa_senior || 0);
    const visite = Number(radarRow.senior_visite || 0);
    const chiusi = Number(radarRow.rilievo_chiusi || 0);
    const nonChiusi = Number(radarRow.rilievo_non_chiusi || 0);
    const real = Number(radarRow.fatturato_eur || 0);

    // 1) volume app vs migliore
    const scoreApp = (app / maxApp) * 100;
    // 2) % passa al senior sugli app
    const scorePassa = app > 0 ? (passa / app) * 100 : 0;
    // 3) % visite rispetto ai passa
    const scoreVisite = passa > 0 ? (visite / passa) * 100 : 0;
    // 4) % chiusi sulle visite
    const scoreChiusi = visite > 0 ? (chiusi / visite) * 100 : 0;
    // 5) % non chiusi sulle visite
    const scoreNonChiusi = visite > 0 ? (nonChiusi / visite) * 100 : 0;
    // 6) Real medio / app vs migliore
    const realPerApp = app > 0 ? real / app : 0;
    const scoreRealMedio = (realPerApp / maxRealPerApp) * 100;

    radarData = {
      labels: [
        "Volume app.",
        "% passa al senior",
        "% visite / passa",
        "% chiusi / visite",
        "% non chiusi / visite",
        "Real medio / app.",
      ],
      datasets: [
        {
          label: radarRow.nome,
          data: [
            scoreApp,
            scorePassa,
            scoreVisite,
            scoreChiusi,
            scoreNonChiusi,
            scoreRealMedio,
          ],
          backgroundColor: "rgba(59,130,246,0.20)",
          borderColor: "rgba(37,99,235,1)",
          pointBackgroundColor: "rgba(37,99,235,1)",
          pointBorderColor: "#ffffff",
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };

    radarOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: `Profilo ${radarRow.nome}`,
          color: "#0f172a",
          font: { size: 16, weight: "600" },
          padding: { bottom: 8 },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const v = Number(ctx.parsed.r || 0);
              return ` ${v.toFixed(1)} / 100`;
            },
          },
        },
      },
      scales: {
        r: {
          suggestedMin: 0,
          suggestedMax: 100,
          ticks: {
            stepSize: 20,
            showLabelBackdrop: false,
            color: "#64748b",
          },
          grid: { color: "rgba(148,163,184,0.35)" },
          angleLines: { color: "rgba(148,163,184,0.35)" },
          pointLabels: {
            color: "#0f172a",
            font: { size: 12 },
          },
        },
      },
    };
  }




  // ================== PIE WOW: DISTRIBUZIONE % ==================
// totali selezionati (li hai già per summary ma qui li ricontiamo sulle righe attive)
const totalRealSelected = activeRows.reduce(
  (sum, r) => sum + (r.fatturato_eur || 0),
  0
);
const totalAppSelected = activeRows.reduce(
  (sum, r) => sum + (r.appuntamenti || 0),
  0
);
const totalKmSelected = activeRows.reduce(
  (sum, r) => sum + (r.km || 0),
  0
);

// scelgo la metrica in base a pieMode
const pieLabels = activeRows.map((r) => r.nome);
let pieRawValues;
let pieTitle;
let pieUnitLabel;

if (pieMode === "real") {
  pieRawValues = activeRows.map((r) => r.fatturato_eur || 0);
  pieTitle = "Peso % del Real (€) per rilevatore";
  pieUnitLabel = "€";
} else if (pieMode === "km") {
  pieRawValues = activeRows.map((r) => r.km || 0);
  pieTitle = "Peso % dei km percorsi per rilevatore";
  pieUnitLabel = "km";
} else {
  // 'app'
  pieRawValues = activeRows.map((r) => r.appuntamenti || 0);
  pieTitle = "Peso % degli appuntamenti per rilevatore";
  pieUnitLabel = "appuntamenti";
}

const pieTotal = pieRawValues.reduce((s, v) => s + v, 0);

const pieData = {
  labels: pieLabels,
  datasets: [
    {
      data: pieRawValues,
      backgroundColor: pieLabels.map((_, i) => {
        const hue = (i * 37) % 360;
        return `hsl(${hue} 80% 60%)`;
      }),
      borderColor: "#ffffff",
      borderWidth: 2,
    },
  ],
};

const pieOptions = {
  plugins: {
    legend: {
      display: true,
      position: "right",
      labels: { color: "#0f172a", boxWidth: 14 },
    },
    title: {
      display: true,
      text: pieTitle,
      color: "#0f172a",
      font: { size: 16, weight: "600" },
      padding: { bottom: 8 },
    },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const v = Number(ctx.raw ?? 0);
          if (!pieTotal) return "0";
          const pct = (v / pieTotal) * 100;

          if (pieMode === "real") {
            const euro = v.toLocaleString("it-IT", {
              style: "currency",
              currency: "EUR",
              maximumFractionDigits: 0,
            });
            return ` ${euro} (${pct.toFixed(1)}%)`;
          }

          if (pieMode === "km") {
            return ` ${v.toLocaleString("it-IT")} km (${pct.toFixed(1)}%)`;
          }

          // appuntamenti
          return ` ${v.toLocaleString("it-IT")} app. (${pct.toFixed(1)}%)`;
        },
      },
    },
  },
};






  // ================== RENDER ==================
  return (
    <div className="st-section">
      {/* KPI */}
      <div className="st-kpi-row">
<KpiCard
  label="Appuntamenti fatti"
  value={summarySelected.tot_appuntamenti}
  caption={showAll ? "Tutti i rilevatori" : "Solo selezionati"}
  active={barMode === "counts" && countMetrics.app}
  onToggle={() => toggleCountMetric("app")}
/>

<KpiCard
  label="Non buoni"
  value={summarySelected.tot_non_buoni}
  caption="Appuntamenti segnati come non buoni"
  active={barMode === "counts" && countMetrics.nonBuoni}
  onToggle={() => toggleCountMetric("nonBuoni")}
/>

<KpiCard
  label="Passa al senior"
  value={summarySelected.tot_passa_senior}
  caption="Appuntamenti passati al senior"
  active={barMode === "counts" && countMetrics.passa}
  onToggle={() => toggleCountMetric("passa")}
/>


<KpiCard
  label="Real (€)"
  value={summarySelected.fatturato_eur}
  format="euro"
  caption="Somma chiuso dai senior"
  active={barMode === "real"}
  onToggle={toggleRealMode}
/>

<KpiCard
  label="Km totali"
  value={summarySelected.km_tot}
  caption="Somma km dei rilevatori"
  active={barMode === "km"}
  onToggle={toggleKmMode}
/>

      </div>

      {/* Riga info selezione */}
      {anyData && !showAll && (
        <div className="st-selection-info">
          {activeRows.length} rilevatori selezionati ·{" "}
          {summarySelected.tot_appuntamenti.toLocaleString("it-IT")}{" "}
          appuntamenti ·{" "}
          {summarySelected.fatturato_eur.toLocaleString("it-IT", {
            style: "currency",
            currency: "EUR",
            maximumFractionDigits: 0,
          })}{" "}
          di Real
        </div>
      )}

      {/* Chips selezione rilevatori */}
      {anyData && (
        <div className="st-chip-strip">
          <button
            type="button"
            className={"st-chip" + (showAll ? " st-chip--active" : "")}
            onClick={() => setVisibleIds(new Set(rows.map((r) => r.id)))}
          >
            Tutti
          </button>
          {rows.map((r) => {
            const id = r.id;
            const on = showAll || visibleIds.has(id);
            return (
              <button
                key={id}
                type="button"
                className={
                  "st-chip" + (on ? " st-chip--active" : " st-chip--muted")
                }
                onClick={() => toggleRilevatore(id)}
                title={`${r.nome} – ${r.appuntamenti} app.`}
              >
                {r.nome}{" "}
                <span className="st-chip-count">
                  {r.appuntamenti.toLocaleString("it-IT")}
                </span>
              </button>
            );
          })}
        </div>
      )}


      {/* Grafici principali */}
      <div className="st-charts-grid">
        <div className="st-chart-card">
          {loading ? (
            <div className="st-chart-placeholder">Caricamento…</div>
          ) : !anyData ? (
            <div className="st-chart-placeholder">
              Nessun dato per i rilevatori nel periodo scelto.
            </div>
          ) : (
            <div className="st-chart-inner" style={{ height: chartHeight }}>
              <Bar data={chartData} options={chartOptions} />
            </div>
          )}
        </div>

        <div className="st-chart-card">
          {loading ? (
            <div className="st-chart-placeholder">Caricamento…</div>
          ) : timelineLabels.length === 0 ? (
            <div className="st-chart-placeholder">
              Nessun dato di fatturato per il periodo selezionato.
            </div>
          ) : (
            <div className="st-chart-inner" style={{ height: chartHeight }}>
              <Line data={timelineData} options={timelineOptions} />
            </div>
          )}
        </div>
      </div>

      {/* Radar profilo rilevatore */}
      {radarRow && radarData && radarOptions && (
        <div className="st-chart-card" style={{ marginTop: 16 }}>
          <div className="st-chart-inner" style={{ height: 360 }}>
            <Radar data={radarData} options={radarOptions} />
          </div>
          <div
            style={{
              fontSize: 12,
              color: "#6b7280",
              marginTop: 6,
              textAlign: "center",
            }}
          >
            Profilo normalizzato di {radarRow.nome} rispetto al migliore del
            periodo (0–100).
          </div>
        </div>
      )}

      {/* Sezione torta + tabella (WOW) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 0.9fr) minmax(0, 1.3fr)",
          gap: 16,
          marginTop: 16,
        }}
      >
        {/* PIE */}
        <div className="st-chart-card">
          {!anyData || pieTotal === 0 ? (
            <div className="st-chart-placeholder">
              Nessun dato per costruire il grafico a torta.
            </div>
          ) : (
            <div
              className="st-chart-inner"
              style={{ height: Math.max(260, activeRows.length * 28) }}
            >
              {/* mini toggle sopra la torta */}
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginBottom: 8,
                  justifyContent: "center",
                  fontSize: 12,
                }}
              >
                <button
                  type="button"
                  className={"chip" + (pieMode === "real" ? " chip-ok" : "")}
                  onClick={() => setPieMode("real")}
                >
                  Real (€)
                </button>
                <button
                  type="button"
                  className={"chip" + (pieMode === "app" ? " chip-ok" : "")}
                  onClick={() => setPieMode("app")}
                >
                  Appuntamenti
                </button>
                <button
                  type="button"
                  className={"chip" + (pieMode === "km" ? " chip-ok" : "")}
                  onClick={() => setPieMode("km")}
                >
                  Km
                </button>
              </div>

              <Pie data={pieData} options={pieOptions} />
            </div>
          )}
        </div>


{/* Tabella riepilogo */}
<div className="st-table-card">
  <div className="st-table-header">
    <h3>Dettaglio per risorsa</h3>
    <p>App., NB, PAS, VS, SA, RC, RNC, Real, Trend e Km.</p>
  </div>

  <div className="st-table-scroll">
    <table className="st-table">
      <thead>
        <tr>
          <th>Rilevatore</th>

          {/* Appuntamenti */}
          <th title="Appuntamenti">App.</th>

          {/* Non buoni */}
          <th title="Appuntamenti non buoni">NB</th>

          {/* Passa al senior */}
          <th title="Appuntamenti passati al senior">PAS</th>

          {/* Visite senior */}
          <th title="Visite senior (appuntamenti seguiti dal senior)">VS</th>

          {/* Senior annullati */}
          <th title="Appuntamenti annullati dal senior">SA</th>

          {/* Rilievi chiusi */}
          <th title="Rilievi chiusi dal senior">RC</th>

          {/* Rilievi non chiusi */}
          <th title="Rilievi non chiusi (senior non chiuso)">RNC</th>

          {/* Real, Trend e Km */}
          <th title="Real (€) chiuso dai senior">Real</th>
          <th title="Trend Real nel periodo">Trend</th>
          <th title="Kilometri percorsi">Km</th>
        </tr>
      </thead>

      <tbody>
        {anyData ? (
          activeRows.map((r) => (
            <tr key={r.id}>
              <td>{r.nome}</td>

              <td>{r.appuntamenti.toLocaleString("it-IT")}</td>
              <td>{r.non_buoni.toLocaleString("it-IT")}</td>
              <td>{r.passa_senior.toLocaleString("it-IT")}</td>
              <td>{r.senior_visite.toLocaleString("it-IT")}</td>
              <td>{r.senior_annullati.toLocaleString("it-IT")}</td>
              <td>{r.rilievo_chiusi.toLocaleString("it-IT")}</td>
              <td>{r.rilievo_non_chiusi.toLocaleString("it-IT")}</td>

              {/* Real € */}
              <td>
                {r.fatturato_eur.toLocaleString("it-IT", {
                  style: "currency",
                  currency: "EUR",
                  maximumFractionDigits: 0,
                })}
              </td>

              {/* Sparkline trend Real */}
              <td style={{ textAlign: "center", minWidth: 90 }}>
                {r.trend_real && r.trend_real.length > 1 ? (
                  <Sparkline values={r.trend_real} />
                ) : (
                  <span
                    style={{ fontSize: 11, color: "#9ca3af" }}
                    title="Trend Real non disponibile"
                  >
                    n/d
                  </span>
                )}
              </td>

              {/* Km totali */}
              <td>{r.km.toLocaleString("it-IT")}</td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={11}>Nessun dato disponibile.</td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
</div>


      </div>

    </div>
  );
}

/* Piccola card KPI clickabile */
function KpiCard({
  label,
  value,
  caption,
  format = "int",
  active = true,
  onToggle,
}) {
  const num = Number(value || 0);
  const display =
    format === "euro"
      ? num.toLocaleString("it-IT", {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
        })
      : num.toLocaleString("it-IT");

  const clickable = typeof onToggle === "function";
  const handlers = clickable
    ? {
        onClick: onToggle,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        },
      }
    : {};

  return (
    <div
      className={
        "st-kpi" +
        (clickable ? " st-kpi--clickable" : "") +
        (!active && clickable ? " st-kpi--off" : "")
      }
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      {...handlers}
    >
      <div className="st-kpi-label">{label.toUpperCase()}</div>
      <div className="st-kpi-value">{display}</div>
      {caption && <div className="st-kpi-caption">{caption}</div>}
      {clickable && (
        <div className="st-kpi-toggle">
          {active ? "Nascondi dal grafico" : "Mostra nel grafico"}
        </div>
      )}
    </div>
  );
}
